import {
  ContextualMenu,
  DirectionalHint,
  IContextualMenuItem,
  mergeStyleSets,
  Text,
  TextField,
} from "@fluentui/react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useMask from "react-mask-hook";
import { useTheme } from "styled-components";
import { IStyledTheme } from "theme/types";
import { StyledIconButton } from "../styled";
import { FieldContainer } from "./FieldContainer";

const getClasses = (theme, isDarkMode, disabled) =>
  mergeStyleSets({
    label: {
      fontSize: "14px",
      fontWeight: "600",
      padding: "5px",
      display: "block",
      color: (function () {
        if (disabled) {
          return theme.palette.neutralSecondaryAlt;
        }
        if (isDarkMode) {
          return "white";
        }
        return theme.palette.black;
      })(),
    },
    labelAsterix: {
      color: theme.palette.redDark,
    },
    fieldContainer: {
      display: "flex",
      flexDirection: "row",
      alignItems: "flex-start",
      gap: "5px",
      width: "100%",
      flexWrap: "nowrap",
    },
    textFieldWrapper: {
      flex: 1,
      minWidth: 0,
    },
  });

const getTextFieldStyle = (transparent, theme, isMarked, confidentiality) => ({
  root: {
    flex: 1,
    minWidth: 0,
    ".ms-TextField-fieldGroup": {
      borderTop: "none !important",
      borderRight: "none !important",
      borderLeft: "none !important",
      background: transparent ? "transparent" : "inherit",
      ...(isMarked && { background: theme.palette.yellowLight }),
      ...(confidentiality && {
        background: `rgb(${confidentiality < 0.9 ? 255 : 200},${
          confidentiality >= 0.8 ? 255 : 200
        },200)`,
      }),
    },
    ".ms-TextField-fieldGroup::after": {
      borderTop: "none !important",
      borderRight: "none !important",
      borderLeft: "none !important",
    },
    ".ms-TextField-fieldGroup input": {
      color: isMarked
        ? "black"
        : theme.isDark
        ? confidentiality
          ? "black"
          : "white"
        : "black",
      width: "100%",
    },
    ".ms-TextField-fieldGroup textarea": {
      color: isMarked
        ? "black"
        : theme.isDark
        ? confidentiality
          ? "black"
          : "white"
        : "black",
      width: "100%",
    },
    ".ms-TextField-fieldGroup input:disabled": {
      background: transparent ? "transparent" : "inherit",
      color: theme.palette.neutralSecondaryAlt,
    },
    ".ms-TextField-fieldGroup textarea:disabled": {
      background: theme.palette.white,
    },
  },
  fieldGroup: {
    width: "100%",
  },
  field: {
    width: "100%",
  },
});

type Props = {
  value: string;
  onChange: (e: React.ChangeEvent<any>) => void;
  name: string;
  disabled?: boolean;
  label: string;
  errors?: any;
  transparent?: boolean;
  required?: boolean;
  multiline?: boolean;
  rows?: number;
  isMarked?: boolean;
  confidentiality?: number;
  options?: string[]; // Added options prop
  [x: string]: any; //rest props
};

const translations = {
  "bfm.reasonForConcludingInsurance.label": "A biztosítás megkötésének oka",
  "bfm.reasonForCancellingInsurance.label": "A biztosítás lemondásának oka",
  "bfm.reasonForConcludingInsurance.options.inheritanceLegallyBinding":
    "Öröklés jogerős hagyatéki alapján",
  "bfm.reasonForConcludingInsurance.options.contractTerminationAnniversary":
    "Biztosítási szerződés évfordulóra történő felmondása",
  "bfm.reasonForConcludingInsurance.options.newVehicleHungary":
    "Magyarországon vásárolt új, 0 km-es gépjármű tulajdonának megszerzése",
  "bfm.reasonForConcludingInsurance.options.usedVehicleHungary":
    "Magyarországon vásárolt használt gépjármű tulajdonának megszerzése",
  "bfm.reasonForConcludingInsurance.options.importedVehicleFirstRegistration":
    "Külföldről behozott és Magyarországon először forgalomba helyezett gépjármű tulajdonának megszerzése",
  "bfm.reasonForConcludingInsurance.options.operatorChange":
    "Üzembentartó-váltás (új üzembentartó bejegyzése)",
  "bfm.reasonForConcludingInsurance.options.contractRenewalAfterNonPayment":
    "Díjnemfizetés miatt megszűnt szerződés újrakötése",
  "bfm.reasonForConcludingInsurance.options.vehicleReinstatementToTraffic":
    "Forgalomból kivont gépjármű forgalomba történő visszahelyezése",
  "bfm.reasonForConcludingInsurance.options.contractRenewalMutualAgreement":
    "Közös megegyezéssel megszűnt szerződés újrakötése",
  "bfm.reasonForConcludingInsurance.options.insuranceTransfer":
    "Biztosítás átkötése",
  "bfm.reasonForConcludingInsurance.options.other": "Egyéb",

  "bfm.reasonForCancellingInsurance.options.noHistory": "Nincs előzmény",
  "bfm.reasonForCancellingInsurance.options.nonPayment": "DNF",
  "bfm.reasonForCancellingInsurance.options.saleExpiration": "Eladás (lejárat)",
  "bfm.reasonForCancellingInsurance.options.loseInterest": "Érdekmúlás",
  "bfm.reasonForCancellingInsurance.options.insuranceCancellationAnniversaryInsurer":
    "Biztosító felm. évfordulóra",
  "bfm.reasonForCancellingInsurance.options.insuranceCancellationAnniversaryClient":
    "Szerződő felm. évfordulóra",
  "bfm.reasonForCancellingInsurance.options.finalWithdrawalFromCirculation":
    "Forgalomból végeleg kivonva",
  "bfm.reasonForCancellingInsurance.options.economicTotalLoss":
    "Gazdasági totálkár",
  "bfm.reasonForCancellingInsurance.options.technicalTotalLoss":
    "Műszaki totálkár",
  "bfm.reasonForCancellingInsurance.options.terminationDueToOperatorChange":
    "Felmondás üzembentartó váltás miatt",
  "bfm.reasonForCancellingInsurance.options.loseInterestDueToOperatorChange":
    "Érdekmúlás üzembentartó váltás miatt",
  "bfm.reasonForCancellingInsurance.options.terminationByMutualAgreement":
    "Megszűnés közös megegyezéssel",
  "bfm.reasonForCancellingInsurance.options.insurerTerminationDueToRisk":
    "Biztosító felmondása kockázati ok miatt",
};

const TextWithOptionsInputField = ({
  disabled,
  label,
  name,
  onChange,
  error = "",
  value,
  errors,
  transparent,
  required,
  isMarked,
  confidentiality,
  isDayMonthField,
  options = [], // Default to empty array
  multiline,
  rows,
  ...props
}: Props) => {
  const [textValue, setTextValue] = useState("");
  const [showContextMenu, setShowContextMenu] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const theme = useTheme() as IStyledTheme;
  const { t } = useTranslation();
  const isDarkMode = theme.isDark;

  const classes = getClasses(theme, isDarkMode, disabled);
  const styles = getTextFieldStyle(
    transparent,
    theme,
    isMarked,
    confidentiality
  );

  // Convert options to contextual menu items
  const menuItems: IContextualMenuItem[] = useMemo(() => {
    return options.map((option) => ({
      key: option,
      text: translations[option],
      onClick: () => {
        const currentValue = textValue || "";
        const selectedText = translations[option];

        // If field is empty, set the selected text
        // If field has content, append with a separator
        const newValue = currentValue.trim()
          ? `${currentValue}\n${selectedText}`
          : selectedText;

        setTextValue(newValue);

        // Create synthetic event for onChange
        const syntheticEvent = {
          target: { name, value: newValue },
          currentTarget: { name, value: newValue },
        } as React.ChangeEvent<any>;

        onChange(syntheticEvent);
        setShowContextMenu(false);
      },
    }));
  }, [options, t, textValue, onChange, name]);

  const handleMenuButtonClick = () => {
    setShowContextMenu(true);
  };

  const handleMenuDismiss = () => {
    setShowContextMenu(false);
  };

  function handleMaskChange(value) {
    setTextValue(value);
  }

  function handleChange(event) {
    setTextValue(event.target.value);
  }

  const maskProps = useMask({
    value: textValue ? textValue?.replace("/", "") : "",
    onChange: handleMaskChange,
    // array of regexp and formatting characters
    mask: "##/##",
    placeholder: "DD/MM",
  });

  useEffect(() => {
    setTextValue(value);
  }, [value]);

  return (
    <FieldContainer isTooltipHidden={disabled} tooltipText={label}>
      <div className={classes.fieldContainer}>
        <div className={classes.textFieldWrapper}>
          <TextField
            key={value}
            id={name + "-input"}
            name={name}
            onBlur={(ev) => {
              onChange(ev);
            }}
            onRenderLabel={() => (
              <Text className={classes.label}>
                {t(label)}{" "}
                <Text className={classes.labelAsterix}>{required && "*"}</Text>
              </Text>
            )}
            styles={styles}
            disabled={disabled}
            label={t(label)}
            onChange={handleChange}
            multiline={multiline}
            rows={rows}
            {...(!isDayMonthField && {
              defaultValue: value ? value?.replace("/", "") : "",
            })}
            errorMessage={name && errors[name]}
            autoComplete="off"
            {...(isDayMonthField && (maskProps as any))}
            {...props}
          />
        </div>

        {options.length > 0 && (
          <>
            <StyledIconButton
              elementRef={menuButtonRef}
              iconProps={{ iconName: "List" }}
              title={
                t("bfm.selectFromOptions.title") ||
                "Select from predefined options"
              }
              ariaLabel={
                t("bfm.selectFromOptions.title") ||
                "Select from predefined options"
              }
              onClick={handleMenuButtonClick}
              disabled={disabled}
              style={{
                marginTop: "2px",
                minWidth: "32px",
                width: "32px",
                height: "32px",
                flexShrink: 0,
              }}
            />

            {showContextMenu && (
              <ContextualMenu
                items={menuItems}
                target={menuButtonRef.current}
                onDismiss={handleMenuDismiss}
                directionalHint={DirectionalHint.bottomLeftEdge}
              />
            )}
          </>
        )}
      </div>
    </FieldContainer>
  );
};

export default TextWithOptionsInputField;
